import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Loader2, Check, BookOpen, Gift } from "lucide-react";
import { Reveal, Label, CurrencyToggle } from "./Reveal";
import { createServiceOrder, verifyServicePayment } from "@/lib/api";
import { loadRazorpay } from "@/lib/razorpay";

const PRICES = {
  inr: { id: "ebook_inr", label: "₹188" },
  usd: { id: "ebook_usd", label: "$9.99" },
};

const POINTS = [
  "Short self help E-book for mothers and parents who want to grow and heal while nurturing their children.",
];

const inputCls = "w-full rounded-xl border border-[#2F3B2E]/20 bg-[#FAF7F0] px-4 py-3 text-sm outline-none focus:border-[#2F3B2E] transition-colors";

export default function Ebook() {
  const [currency, setCurrency] = useState("inr");
  const [email, setEmail] = useState("");
  const [gift, setGift] = useState(false);
  const [recipientName, setRecipientName] = useState("");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [giftMessage, setGiftMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => { loadRazorpay(); }, []);

  const buy = async () => {
    if (!email) {
      toast.error("Please enter your email — your receipt will be sent there.");
      return;
    }
    if (gift && !recipientEmail) {
      toast.error("Please enter your friend's email so we can deliver your gift.");
      return;
    }
    setLoading(true);
    try {
      const ok = await loadRazorpay();
      if (!ok) throw new Error("script");
      const { data } = await createServiceOrder({
        package_id: PRICES[currency].id, email, origin_url: window.location.origin,
        gift, recipient_name: recipientName || undefined,
        recipient_email: recipientEmail || undefined, gift_message: giftMessage || undefined,
      });
      const rzp = new window.Razorpay({
        key: data.key_id, amount: data.amount, currency: data.currency,
        name: "Mended Alchemy",
        description: gift ? "Mumma Can Heal Me — A Gift" : "Mumma Can Heal Me — Ebook",
        order_id: data.order_id,
        prefill: { email },
        theme: { color: "#2F3B2E" },
        modal: { ondismiss: () => setLoading(false) },
        handler: async (r) => {
          try {
            await verifyServicePayment({
              razorpay_order_id: r.razorpay_order_id,
              razorpay_payment_id: r.razorpay_payment_id,
              razorpay_signature: r.razorpay_signature,
              email,
            });
            setDone(true);
            toast.success(gift
              ? "Payment complete! Your gift is on its way to their inbox."
              : "Payment complete! Your download link is on its way to your inbox.");
          } catch {
            toast.error("Payment received but verification failed — we'll complete delivery by email shortly.");
          } finally { setLoading(false); }
        },
      });
      rzp.on("payment.failed", () => { toast.error("Your payment wasn't completed."); setLoading(false); });
      rzp.open();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout. Please try again.");
      setLoading(false);
    }
  };

  return (
    <section id="ebook-section" data-testid="ebook-section" className="py-20 lg:py-32 px-4 sm:px-8 lg:px-12">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <Reveal className="lg:col-span-5">
          <div className="relative mx-auto max-w-xs lg:max-w-sm">
            <div className="absolute -inset-4 rounded-[2rem] bg-[#2F3B2E]/5 rotate-3" />
            <img
              src="/ebook-cover.jpg"
              alt="Mumma Can Heal Me ebook cover"
              data-testid="ebook-cover"
              className="relative w-full rounded-2xl border border-[#2F3B2E]/15 shadow-[0_30px_60px_-20px_rgba(47,59,46,0.35)] -rotate-2 hover:rotate-0 transition-transform duration-500"
            />
          </div>
        </Reveal>

        <Reveal delay={0.12} className="lg:col-span-7">
          <Label>Products</Label>
          <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight leading-tight text-[#3B352D]">
            Mumma Can Heal Me
          </h2>
          <p className="mt-5 max-w-xl text-base sm:text-lg leading-relaxed text-[#6B665C]">
            A gentle companion for every mother who has ever felt overwhelmed.
            Begin healing yourself and watch your children bloom.
          </p>
          <ul className="mt-7 space-y-3">
            {POINTS.map((p) => (
              <li key={p} className="flex items-start gap-2.5 text-sm text-[#3B352D]/85">
                <Check size={17} className="text-[#A8916B] shrink-0 mt-0.5" />
                <span>{p}</span>
              </li>
            ))}
          </ul>

          {done ? (
            <div data-testid="ebook-success" className="mt-9 rounded-2xl border border-[#2F3B2E]/15 bg-white p-6">
              <div className="flex items-center gap-3">
                {gift ? <Gift className="text-[#2F3B2E]" size={22} /> : <BookOpen className="text-[#2F3B2E]" size={22} />}
                <p className="font-serif-display text-xl text-[#2F3B2E]">
                  {gift ? "Your gift is on its way." : "Thank you! Your ebook is on its way."}
                </p>
              </div>
              <p className="mt-2 text-sm text-[#6B665C]">
                {gift
                  ? <>We've emailed the ebook to <strong>{recipientEmail}</strong> with your gift note, and sent a confirmation to <strong>{email}</strong>.</>
                  : <>We've emailed your download link to <strong>{email}</strong>. Please check your inbox (and spam folder, just in case).</>}
              </p>
            </div>
          ) : (
            <div className="mt-9 rounded-2xl border border-[#2F3B2E]/15 bg-white p-6">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <p className="font-serif-display text-3xl text-[#2F3B2E]" data-testid="ebook-price">{PRICES[currency].label}</p>
                <CurrencyToggle currency={currency} onChange={setCurrency} compact />
              </div>

              <div className="mt-4 space-y-3">
                <input
                  type="email"
                  data-testid="ebook-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email (for the receipt)"
                  className={inputCls}
                />

                <button
                  type="button"
                  onClick={() => setGift((g) => !g)}
                  data-testid="ebook-gift-toggle"
                  className={`w-full inline-flex items-center justify-center gap-2 rounded-xl border px-4 py-3 text-sm font-medium transition-colors duration-200 ${gift ? "border-[#A8916B] bg-[#A8916B]/10 text-[#2F3B2E]" : "border-[#2F3B2E]/20 text-[#2F3B2E]/75 hover:border-[#A8916B]"}`}
                >
                  <Gift size={16} className="text-[#A8916B]" />
                  {gift ? "Sending as a gift — lovely choice" : "Send it as a gift to a mum you love"}
                </button>

                {gift && (
                  <div className="space-y-3 rounded-xl border border-[#A8916B]/30 bg-[#A8916B]/5 p-4" data-testid="ebook-gift-fields">
                    <input
                      data-testid="ebook-recipient-name"
                      value={recipientName}
                      onChange={(e) => setRecipientName(e.target.value)}
                      placeholder="Her name"
                      className={inputCls}
                    />
                    <input
                      type="email"
                      data-testid="ebook-recipient-email"
                      value={recipientEmail}
                      onChange={(e) => setRecipientEmail(e.target.value)}
                      placeholder="Her email (we deliver the ebook here)"
                      className={inputCls}
                    />
                    <textarea
                      data-testid="ebook-gift-message"
                      value={giftMessage}
                      onChange={(e) => setGiftMessage(e.target.value)}
                      rows={2}
                      placeholder="A short note, from you (optional)"
                      className={inputCls + " resize-none"}
                    />
                  </div>
                )}
              </div>

              <button
                onClick={buy}
                disabled={loading}
                data-testid="ebook-buy-button"
                className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#2F3B2E] px-7 py-3.5 text-sm font-medium text-white hover:bg-[#232B22] transition-colors duration-200 disabled:opacity-70"
              >
                {loading && <Loader2 size={16} className="animate-spin" />}
                {loading ? "Opening checkout…" : gift ? `Gift the Ebook · ${PRICES[currency].label}` : `Get the Ebook · ${PRICES[currency].label}`}
              </button>
              <p className="mt-3 text-center text-xs text-[#6B665C]">Secure payment via Razorpay · Instant email delivery</p>
            </div>
          )}
        </Reveal>
      </div>
    </section>
  );
}
